import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ScaleRangeStepper } from "components/__haim/Common/ScaleStepper";
import { floors, Floor } from "../../../@types/sharedSchema";

export interface FloorRangePickerProps {
  initialMin?: Floor;
  initialMax?: Floor;
  disabled?: boolean;
  onRangeChange?: (min: Floor | undefined, max: Floor | undefined) => void;
}

/**
 * Floors min/max as +/− steppers.
 *
 * Was a pair of 31-entry `Select`s that each carried their own half of the
 * min<=max correction. Now it shares the contact dialog's stepper and the
 * single correction implementation in ScaleRangeStepper — floors were the
 * easiest place to produce an inverted range, since both dropdowns could be
 * changed independently.
 *
 * The scale is the shared `floors` enum, so stepping never lands on a value the
 * filter cannot represent. That enum is CONTIGUOUS (-4 … 50) — it used to jump
 * 20 → 25 → 30 … which, since ScaleStepper walks its scale by index, made every
 * tap above floor 20 move five floors at a time. See the comment on `floors` in
 * sharedSchema for why the domain was densified instead of giving this picker a
 * private integer scale.
 */
export default function FloorRangePicker({
  initialMin,
  initialMax,
  disabled,
  onRangeChange
}: FloorRangePickerProps) {
  const { t } = useTranslation();
  const [range, setRange] = useState<{ min: number | null; max: number | null }>({
    min: initialMin ?? null,
    max: initialMax ?? null
  });

  const handleChange = (min: number | null, max: number | null): void => {
    setRange({ min, max });
    onRangeChange?.((min ?? undefined) as Floor, (max ?? undefined) as Floor);
  };

  return (
    <ScaleRangeStepper
      scale={floors as unknown as number[]}
      min={range.min}
      max={range.max}
      disabled={disabled}
      minLabel={t("listingsMatch.filters.minFloorLabel")}
      maxLabel={t("listingsMatch.filters.maxFloorLabel")}
      formatValue={(value) => (value === floors[floors.length - 1] ? `${value}+` : value)}
      onChange={handleChange}
    />
  );
}
